import { useEffect, useMemo, useRef, useState } from "react";
import ReportViewer from "../../../../components/ReportViewer";
import SuperBillReport from "../../../../components/SuperBillReport";
import { useGetProviderGroupProfile } from "../../../../queries/admin/provider-group/use-get-provider-group-profile";
import { useGetSuperBill } from "../../../../queries/billings/use-get-super-bill";
import {
  DEFAULT_VALUE_LABEL_LIST,
  getXtenantId,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import { GetSuperBillConst } from "./add-super-bill-const";
import { convertDiagnosisCodesData } from "./claims/claims-constants";
import { useRenderingProviderInfo } from "./use-rendering-provider-info";

interface PrintSuperBillProps {
  superBillUuid: string | null;
  patientName?: string; // Optional: patient name from the list for better display
  onClose?: () => void;
}

/**
 * Component to handle printing SuperBill
 * Takes UUID as input and handles all API calls, data transformation, and PDF generation
 */
const PrintSuperBill = ({
  superBillUuid,
  patientName,
  onClose,
}: PrintSuperBillProps) => {
  const pdfRef = useRef<{ generatePdf: () => void }>(null);
  const [shouldGeneratePdf, setShouldGeneratePdf] = useState(true);
  const { data: superBillData, isLoading: isLoadingSuperBill } =
    useGetSuperBill({
      superBillUuid: superBillUuid ?? "",
      xTENANTID: getXtenantId(),
    });

  const { data: providerSidePgProfileData } =
    useGetProviderGroupProfile(getXtenantId());

  // NPI / License TX from the provider record (superBill returns license null).
  const renderingProviderInfo = useRenderingProviderInfo(superBillData?.data);

  // todo: we need to remove this conversion and map inside superbill
  // Transform super bill data to formik-like structure
  const transformedFormData = useMemo(() => {
    if (!superBillData?.data) return null;

    const data = superBillData.data as any;

    return {
      dateGenerated: data.billDate ?? undefined,
      uuid: data.patient ? { [data.patient]: patientName || "" } : undefined,
      locationId: data.location ?? undefined,
      placeOfService: data.placeOfService ?? undefined,
      dateOfService: data.dateOfService ? new Date(data.dateOfService) : null,
      serviceState: data.serviceState ?? undefined,
      renderingproviderId: data.renderingProvider ?? undefined,
      renderingProviderNpi:
        renderingProviderInfo?.npi ?? data.renderingProviderNpi ?? undefined,
      renderingProviderLicenseNumber:
        renderingProviderInfo?.licenseNumber ??
        data.renderingProviderLicenseNumber ??
        undefined,
      priorAuthorization: data.priorAuthorization ?? undefined,
      billingProviderId: data.billingProvider ?? undefined,
      billingProvideNPINumber: data.billingProviderNpi ?? undefined,
      referringProviderId: data.referringProvider ?? undefined,
      referringProviderNPINumber: data.referringProviderNpi ?? undefined,
      orderingProviderId: data.orderingProvider ?? undefined,
      orderingProviderNPINumber: data.orderingProviderNpi ?? undefined,
      diagnisisCodeData: data.diagnosisCodes
        ? convertDiagnosisCodesData(data.diagnosisCodes)
        : undefined,
      diagnosisCodes: data.diagnosisCodes
        ? convertDiagnosisCodesData(data.diagnosisCodes)
        : undefined,
      subTotal: data.subTotal?.toString() ?? undefined,
      advancedPaid: data.advancedPaid?.toString() ?? undefined,
      insuranceAmount: data.insuranceAmount?.toString() ?? undefined,
      previousBalance: data.previousBalance?.toString() ?? undefined,
      total: data.total?.toString() ?? undefined,
      patientBalance: data.patientBalance?.toString() ?? undefined,
      ecarehealthPatientId: data.ecarehealthPatientId ?? undefined,
      patientDOB: data.patientDOB ?? undefined,
      patientAddress: data.patientAddress ?? undefined,
      patientPhone: data.patientPhone ?? undefined,
      patientId: data.patient ?? undefined,
      prevPaid: data.prevPaid?.toString() ?? undefined,
    };
  }, [superBillData, patientName, renderingProviderInfo]);

  // Transform procedure codes
  const transformedProcedureCodes = useMemo(() => {
    const data = superBillData?.data as any;
    if (!data?.patientProcedureCodes) return undefined;

    return data.patientProcedureCodes.map((item: GetSuperBillConst) => ({
      ...item,
      modifiers: transformMultiSelectFrontEndVal(
        DEFAULT_VALUE_LABEL_LIST,
        item?.modifiers
      ),
    }));
  }, [superBillData]);

  // Reset shouldGeneratePdf when UUID changes
  useEffect(() => {
    if (superBillUuid) {
      setShouldGeneratePdf(true);
    }
  }, [superBillUuid]);

  // Generate PDF once data is loaded
  useEffect(() => {
    if (
      superBillUuid &&
      transformedFormData &&
      transformedProcedureCodes &&
      !isLoadingSuperBill &&
      shouldGeneratePdf
    ) {
      // Small delay to ensure ReportViewer is ready
      setTimeout(() => {
        pdfRef.current?.generatePdf();
        setShouldGeneratePdf(false);
      }, 100);
    }
  }, [
    superBillUuid,
    transformedFormData,
    transformedProcedureCodes,
    isLoadingSuperBill,
    shouldGeneratePdf,
  ]);

  if (!superBillUuid) {
    return null;
  }

  return (
    <ReportViewer
      pdfRef={pdfRef}
      loading={isLoadingSuperBill}
      onClose={onClose}
      title="Invoice Details"
    >
      {transformedFormData && transformedProcedureCodes && (
        <SuperBillReport
          providerGroupData={providerSidePgProfileData}
          formData={transformedFormData as any}
          procedureCodes={transformedProcedureCodes}
        />
      )}
    </ReportViewer>
  );
};

export default PrintSuperBill;
